import {
  Flag,
  FullApplication,
  Occupancy,
  Persona,
  PurchaseType,
  RemortgageReason,
} from "models/FullApplication"
import { ApplicationReason, ApplicationSummary } from "models/ApplicationSummary"
import { ApplicationSummaryEnhanced } from "models/ApplicationSummaryEnhanced"
import { Cohort } from "models/Cohort"
import { DateDiff } from "lib/Date"
import { MY_MOJO_API } from "../Config"
import { axiosInstance } from "lib/Api"
import { isDevModeEnabled } from "lib/Cookie"
import * as DevModeService from "./DevMode/DevModeService"

export async function GetAllApplications(
  invalidateCache = false
): Promise<ApplicationSummaryEnhanced[]> {
  const response = await axiosInstance.get(`${MY_MOJO_API}/applications/sf`, {
    headers: {
      "Cache-Control": invalidateCache ? "no-store, max-age=0" : null,
    },
    withCredentials: true,
  })

  return isDevModeEnabled() ? DevModeService.applySummaryScenario(response.data) : response.data
}

export async function GetApplication(
  applicationId: string,
  invalidateCache = false
): Promise<FullApplication> {
  if (isDevModeEnabled()) {
    return await DevModeService.getApplication()
  }

  const response = await axiosInstance.get(`${MY_MOJO_API}/applications/${applicationId}`, {
    headers: {
      "Cache-Control": invalidateCache ? "no-store, max-age=0" : null,
    },
    withCredentials: true,
  })

  return response.data
}

export async function UpdateApplication(application: FullApplication): Promise<FullApplication> {
  const response = await axiosInstance.put(
    `${MY_MOJO_API}/applications/${application.applicationId}`,
    {
      application: application,
    },
    {
      headers: {},
      withCredentials: true,
    }
  )

  return response.data
}

export async function SetApplicationFlags(
  application: FullApplication,
  flags: Flag[]
): Promise<FullApplication> {
  const result = await axiosInstance.put(
    `${MY_MOJO_API}/applications/${application.applicationId}/flags?flags=${flags.join(",")}`,
    null,
    {
      headers: {},
      withCredentials: true,
    }
  )

  return result.data as FullApplication
}

export async function PushNewApplicationFlag(
  application: FullApplication,
  flag: Flag
): Promise<FullApplication> {
  const flagsToSet = []
  const existingFlags = application?.metadata?.flags ?? []

  if (existingFlags.includes(flag)) {
    return application
  }

  if (existingFlags?.length > 0) {
    flagsToSet.push(existingFlags)
  }

  flagsToSet.push(flag)

  const result = await axiosInstance.put(
    `${MY_MOJO_API}/applications/${application.applicationId}/flags?flags=${flagsToSet.join(",")}`,
    null,
    {
      headers: {},
      withCredentials: true,
    }
  )

  return result.data as FullApplication
}

export function HasApplicationExpired(
  application: ApplicationSummary | ApplicationSummaryEnhanced
): boolean {
  const referenceDate = application?.lastModifiedDate ?? application?.createdOn
  return DateDiff(new Date(), referenceDate, "month") > 6
}

export function GetApplicationCohorts(
  application: ApplicationSummaryEnhanced | FullApplication
): Cohort[] {
  if (!application) {
    return []
  }

  const cohorts: Cohort[] = []
  const isFirstTimeBuyer =
    (application?.borrowingDetails?.firstTimeBuyer || application?.purchaseType) ===
    PurchaseType.FirstTimeBuyer
  if (isFirstTimeBuyer) {
    addUnique(Cohort.FirstTimeBuyer)
  }

  if (application.applicationReason === ApplicationReason.LookingForProperty) {
    addUnique(Cohort.NotFoundAProperty)
    addUnique(Cohort.Purchase)
    addUnique(Cohort.PreOffer)
  }

  if (application.applicationReason === ApplicationReason.FoundProperty) {
    addUnique(Cohort.FoundAProperty)
    addUnique(Cohort.Purchase)
  }

  if (application.persona === Persona.OfferAccepted) {
    addUnique(Cohort.PostOffer)
  }

  if (application.persona === Persona.FoundProperty) {
    addUnique(Cohort.FoundAProperty)
    addUnique(Cohort.PreOffer)
  }

  if ([Persona.StartedLooking, Persona.ActivelyLooking].includes(application.persona)) {
    addUnique(Cohort.NotFoundAProperty)
    addUnique(Cohort.PreOffer)
  }

  if (application?.purchaseType === PurchaseType.HomeMove) {
    addUnique(Cohort.MovingHome)
    addUnique(Cohort.Purchase)
  }

  if (application?.purchaseType === PurchaseType.SecondHome) {
    addUnique(Cohort.SecondHome)
    addUnique(Cohort.Purchase)
  }

  if (application.applicationReason === ApplicationReason.Remortgage) {
    addUnique(Cohort.Remortgage)

    if (DaysRemainingInErc(application) > 1) {
      addUnique(Cohort.StuckInERC)
    }

    switch (application.remortgageReason) {
      case RemortgageReason.Additional_borrowing:
        addUnique(Cohort.AdditionalBorrowing)
        break
      case RemortgageReason.Release_money:
        addUnique(Cohort.ReleaseMoney)
        break
      default:
        addUnique(Cohort.LikeForLike)
        break
    }
  }

  const isBuyToLet =
    (application?.basicPropertyDetails?.occupancy || application?.occupancy) === Occupancy.RentItOut
  if (isBuyToLet) {
    addUnique(Cohort.BuyToLet)
  }

  return cohorts

  function addUnique(cohort: Cohort) {
    if (cohorts.indexOf(cohort) < 0) {
      cohorts.push(cohort)
    }
  }
}

export function IsRemo(application: FullApplication): boolean {
  const cohorts = GetApplicationCohorts(application)
  return cohorts.includes(Cohort.Remortgage)
}

export function IsRemoEnhanced(application: ApplicationSummaryEnhanced): boolean {
  return application.applicationReason === ApplicationReason.Remortgage
}

export function IsPreOffer(application: FullApplication): boolean {
  const cohorts: Cohort[] = GetApplicationCohorts(application)
  return !!cohorts.includes(Cohort.PreOffer)
}

export function IsERC(application: FullApplication) {
  const ercDate = application?.remortgageDetails?.initialPeriodEndDate
  const months = DateDiff(new Date(), ercDate, "month")

  return months > 6
}

export function DaysRemainingInErc(
  application: FullApplication | ApplicationSummaryEnhanced
): number {
  const endDate =
    application?.remortgageDetails?.initialPeriodEndDate ?? application?.initialPeriodEndDate
  if (!endDate) {
    return null
  }
  return DateDiff(new Date(), endDate, "day")
}

export function NeedsMip(application: ApplicationSummaryEnhanced): boolean {
  if (application.mipUrl) {
    return true
  }

  return false
}

export function HasCompletedFactFind(
  application: FullApplication | ApplicationSummaryEnhanced
): boolean {
  if ("firstTimeBuyer" in application) {
    return application.firstTimeBuyer
  }

  return application?.metadata?.flags?.includes(Flag.CompletedFactFindPreferences) ?? false
}

export function GetApplicationTypeString(
  application: FullApplication | ApplicationSummaryEnhanced
): string {
  if ("firstTimeBuyer" in application && application.firstTimeBuyer) {
    return "First time buyer application"
  }

  if ("borrowingDetails" in application && application.borrowingDetails?.firstTimeBuyer) {
    return "First time buyer application"
  }

  if ("purchaseType" in application && application.purchaseType) {
    return "Purchase application"
  }

  if (application.applicationReason === ApplicationReason.Remortgage) {
    return "Remortgage application"
  }

  return "Application"
}

// NOTE: this definition for "active case" is also used in the funnel active-case endpoint. If you need to update this logic, also consider updating the logic in that endpoint
export function IsActiveApplication(application: ApplicationSummaryEnhanced): boolean {
  if (!application) {
    return false
  }

  const createdDate = application.createdOn ? new Date(application.createdOn) : null
  const ninetyDaysAgo = new Date()
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90)

  const isCreatedWithinLast90Days = createdDate && createdDate >= ninetyDaysAgo
  const hasPreviousOwner =
    application.previousOpportunityOwner !== null &&
    application.previousOpportunityOwner !== undefined
  const isNotMojoOwned = application.previousOpportunityOwner !== "Mojo Mortgages"
  const isNotClosedLost = application.stage !== "Closed Lost"
  const isNotClosedWon = application.stage !== "Closed Won"

  return (
    isCreatedWithinLast90Days &&
    hasPreviousOwner &&
    isNotMojoOwned &&
    isNotClosedLost &&
    isNotClosedWon
  )
}

export function GetActiveApplications(
  applications: ApplicationSummaryEnhanced[]
): ApplicationSummaryEnhanced[] {
  return applications.filter(IsActiveApplication)
}
